/*
# -----------------------------------------------------------------------------
# Component: receiptsAjaxMixin.js
# Author: antonio.ramirez@pst.sg
# Created Date: 06/May/2019
# Comments: Responsible for all backend related calls for Manage Receipts
# -----------------------------------------------------------------------------
*/
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { formatDisplayDate, formatDisplayDateTime2, formatDisplayDateTime4, removeTime } from '@/utils/date'
import clone from 'clone'
import { formatPrice } from '@/utils/index'
import { removeFormatNumber } from '@/utils/number'
import { MATCH_LOAD_DATA, CHG_ACCT_MCH_RECKEY } from '@/store/types'
import { recordLockMixin } from '@/mixins/common/recordLockMixin'
import { accountsAjaxCalls } from '@/mixins/accounting/accountsAjaxCalls'
import { strJoin } from '@/utils/stringutil'

export const receiptsAjaxMixin = {
  data() {
    return {
      actionName: ''
    }
  },
  computed: {
    receiptData() {
      return this.$store.state.receipt.receiptData
    }
  },
  created() {
    // AR: Initialize CommonUtil Menu 29-Jan-2021
    this.initCommonUtilMenu()
  },
  methods: {
    async performSearch() {
      const apiUrl = this.$store.state.receipt.menu.performSearch
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.pagination
          const paramMap = {
            page: page - 1,
            size: itemsPerPage
          }
          let massageFilters = clone(this.receiptsForm)
          if (massageFilters.transactionDateFrom !== null && massageFilters.transactionDateFrom !== '') {
            massageFilters.transactionDateFrom = formatDisplayDateTime2(removeTime(massageFilters.transactionDateFrom))
          }

          if (massageFilters.transactionDateTo !== null && massageFilters.transactionDateTo !== '') {
            massageFilters.transactionDateTo = formatDisplayDateTime2(removeTime(massageFilters.transactionDateTo))
          }
          const result = await this.$http.get(filterQuery(expandURI(apiUrl, paramMap), massageFilters, this.pagination.sortBy, this.pagination.sortDesc))
          const { data } = result.data
          if (data) {
            this.totalItems = result.data.totalElements
            this.items = []
            data.map((_result) => {
              const resultList = {
                branch: _result.branch,
                acctNo: _result.acctNo,
                docNo: _result.docNo,
                type: _result.type,
                suffix: _result.suffix,
                fund: _result.fund,
                tranDate: formatDisplayDate(_result.tranDate),
                curr: _result.curr,
                amount: formatPrice(_result.debitAmt),
                gst: formatPrice(_result.othGST),
                bankAmount: formatPrice(_result.bankOrigAmt),
                localBankAmount: formatPrice(_result.bankLocTotal)
              }
              this.items.push(resultList)
            })
          } else {
            this.items = []
          }
        } catch (err) {
          //
          this.errorMessage(err)
        } finally {
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        this.loading = false
      }
    },
    // Note: Same method for Create and Update
    async performSave() {
      const apiURI = this.$store.state.receipt.menu.performAction
      if (apiURI) {
        try {
          if (this.receiptsForm.transactionDateDisp !== '') {
            this.receiptsForm.transactionDate = formatDisplayDate(this.receiptsForm.transactionDateDisp)
          }
          const result = await this.$http.post(apiURI, this.receiptsForm)
          const { data } = result
          if (data) {
            let docNo = this.getDocumentNo(this.receiptsForm.documentNo, data.resultMap.documentNo, this.receiptsForm.actionName)
            this.receiptsForm.documentNo = data.resultMap.documentNo

            let recKey = strJoin('|', this.receiptsForm.transactionType, this.receiptsForm.branch,
              this.receiptsForm.accountNo, docNo, this.receiptsForm.fund, this.receiptsForm.accountNoSuffix)

            if (this.receiptsForm.actionName === 'CREATE_MATCH_NOW') {
              this.$store.commit(CHG_ACCT_MCH_RECKEY, recKey)
              this.checkRecordLock(recKey, 'ACT')
            }

            if (this.receiptsForm.printDocument === 'I' && (this.receiptsForm.actionName === 'CREATE_SAVE_POST' ||
            this.receiptsForm.actionName === 'UPDATE_SAVE_POST' || this.receiptsForm.actionName === 'CREATE_MATCH_LATER' ||
            this.receiptsForm.actionName === 'UPDATE_MATCH_LATER')) {
              const commit = {
                branch: this.receiptsForm.branch,
                accountNo: this.receiptsForm.accountNo,
                type: this.receiptsForm.transactionType,
                documentNo: this.receiptsForm.documentNo,
                fund: this.receiptsForm.fund,
                suffix: this.receiptsForm.accountNoSuffix,
                actionName: this.receiptsForm.actionName,
                printDoc: this.receiptsForm.printDocument
              }
              this.doExportAcctDoc(commit)
            }
            this.notify(data)
            if (!(this.receiptsForm.actionName === 'CREATE_SAVE_POST' ||
              this.receiptsForm.actionName === 'CREATE_SAVE_REVIEW' ||
              this.receiptsForm.actionName === 'CREATE_MATCH_NOW' ||
              this.receiptsForm.actionName === 'UPDATE_MATCH_NOW') &&
              (docNo !== '' && docNo !== undefined && docNo !== null)) {
              // AR: OS-660 Unlock Record upon exit 29-Jan-2021
              this.unlockRecord(recKey, 'ACT')
              this.$store.commit(CHG_ACCT_MCH_RECKEY, '')
            }
            this.doExit()
            if (this.receiptsForm.actionName === 'CREATE_MATCH_NOW' || this.receiptsForm.actionName === 'UPDATE_MATCH_NOW') {
              this.doMatchNow(this.receiptsForm)
            } else {
              this.$router.push({ name: 'receipts' })
            }
          }
        } catch (err) {
          const { data } = err.response
          this.showPromptLeave = true
          if (data) {
            this.validStatus.optional = true
            this.processingFlag = false
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.showPromptLeave = true
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async performReview() {
      const apiURI = this.$store.state.receipt.menu.performAction
      if (apiURI) {
        try {
          this.receiptsForm.actionName = 'RECEIPT_REVIEW'
          this.receiptsForm.branch = this.receiptData.branch
          this.receiptsForm.accountNo = this.receiptData.acctNo
          this.receiptsForm.transactionType = this.receiptData.type
          this.receiptsForm.documentNo = this.receiptData.docNo
          this.receiptsForm.fund = this.receiptData.fund
          this.receiptsForm.accountNoSuffix = this.receiptData.suffix
          const response = await this.$http.post(apiURI, this.receiptsForm)
          const { data } = response.data
          if (data) {
            this.$set(this, 'receiptsForm', data.accounts)
            this.receiptsForm.amount = formatPrice(data.accounts.amount)
            this.receiptsForm.gstAmount = formatPrice(data.accounts.gstAmount)
            this.receiptsForm.netAmount = formatPrice(data.accounts.netAmount)
            this.receiptsForm.bankAmount = formatPrice(data.accounts.bankAmount)
            this.receiptsForm.bankCharges = formatPrice(data.accounts.bankCharges)
            this.receiptsForm.bankTotalLocal = formatPrice(data.accounts.bankTotalLocal)
            this.receiptsForm.docAE = data.accounts.docAE === 'true'
            this.receiptsForm.gstAmountAE = data.accounts.gstAmountAE === 'true'
            this.receiptsForm.bankAmountAE = data.accounts.bankAmountAE === 'true'
            this.receiptsForm.bankTotalLocalAE = data.accounts.bankTotalLocalAE === 'true'
            this.receiptsForm.policyNo = ''
            if (data.accounts.polNo !== '' && data.accounts.polNo !== null) {
              this.receiptsForm.policyNo = data.accounts.polNo + '-' + data.accounts.renCnt + '-' + data.accounts.endtCnt
            }
            this.receiptsForm.transactionDate = data.accounts.transactionDate
            this.receiptsForm.transactionDateDisp = data.accounts.transactionDate
            if (data.accounts.fund === null) {
              this.receiptsForm.oldFund = ''
            } else {
              this.receiptsForm.oldFund = data.accounts.fund
            }
            if (data.accounts.accountNoSuffix === null) {
              this.receiptsForm.oldAccountNoSuffix = ''
            } else {
              this.receiptsForm.oldAccountNoSuffix = data.accounts.accountNoSuffix
            }
            if (data.accounts.bankCharges === null || data.accounts.bankCharges === 'NaN') {
              this.receiptsForm.bankCharges = '0.00'
            }
            if (data.accounts.gstRate === null || data.accounts.gstRate === 'NaN') {
              this.receiptsForm.gstRate = '0.00'
            }
            if (data.accounts.netAmount === null || data.accounts.netAmount === 'NaN') {
              this.receiptsForm.netAmount = '0.00'
            }
            if (data.accounts.bankAmount !== '' && data.accounts.bankAmount !== null) {
              this.receiptsForm.bankTotalAmount = formatPrice(Number(removeFormatNumber(data.accounts.bankAmount)) + Number(removeFormatNumber(data.accounts.bankCharges)))
            }
            this.fetchUserDetails(data.accounts.createdBy).then(result => {
              this.receiptsForm.createdBy = result
            })
            this.receiptsForm.createdDate = formatDisplayDateTime4(data.accounts.createdDate)
            this.fetchUserDetails(data.accounts.modifiedBy).then(result => {
              this.receiptsForm.modifiedBy = result
            })
            this.receiptsForm.modifiedDate = formatDisplayDateTime4(data.accounts.modifiedDate)
            this.getPaymentMethod(data.accounts.paymentMethod)
          }
        } catch (err) {
          const { data } = err.response
          this.showPromptLeave = true
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.showPromptLeave = true
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getAcctMasterRcptChequeNo(form) {
      const apiUrl = this.$store.state.receipt.menu.checkAcctMasterCheqNo
      if (apiUrl) {
        try {
          const requestParams = {
            payersChequeNo: form.payersChequeNo,
            bankNo: form.bankNo,
            branch: form.branch,
            tranType: form.transactionType,
            docNo: form.documentNo,
            acctNo: form.accountNo,
            fund: form.fund,
            suffix: form.accountNoSuffix
          }
          const response = await this.$http.get(expandURI(apiUrl, requestParams))
          const { data } = response.data
          if (data) {
            this.isExistChequeNo = data
          }
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
          return false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        return false
      }
    },
    async doMatchNow(data) {
      await Promise.all([this.$store.dispatch('matchingInit')])
      const commit = {
        action: 'M',
        branch: data.branch,
        acctNo: data.accountNo,
        type: data.transactionType,
        docNo: data.documentNo,
        fund: data.fund,
        suffix: data.accountNoSuffix,
        frmModule: 'AcctRcpt',
        printDoc: data.printDocument
      }
      this.$store.commit(MATCH_LOAD_DATA, commit)
      this.$router.push({ name: 'reviewMatchTransaction' })
    },
    async fetchBranch() {
      this.branches = []
      let apiUrl = this.$store.state.receipt.menu.fetchBranch
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchFund() {
      this.funds = []
      let apiUrl = this.$store.state.receipt.menu.fetchFund
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.funds.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAcctNoSfx() {
      this.acctnosuffixes = []
      let apiUrl = this.$store.state.receipt.menu.fetchAcctNoSfx
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.acctnosuffixes.push(data)
              })
            }
          }
        })
      }
    },
    async fetchGstStatus() {
      this.gstStatuses = []
      let apiUrl = this.$store.state.receipt.menu.fetchGstStatus
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.gstStatuses.push(data)
              })
            }
          }
        })
      }
    },
    async fetchCurrency() {
      this.currencies = []
      let apiUrl = this.$store.state.receipt.menu.fetchCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.currencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAmountCurrency() {
      this.amountcurrencies = []
      let apiUrl = this.$store.state.receipt.menu.fetchAmountCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.amountcurrencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchPaymentMethod() {
      this.paymentmethods = []
      let apiUrl = this.$store.state.receipt.menu.fetchPaymentMethod
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.paymentmethods.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAccount() {
      this.accounts = []
      let apiUrl = this.$store.state.receipt.menu.fetchAccount
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.accounts.push(data)
              })
            }
          }
        })
      }
    },
    async fetchDepartment() {
      this.departments = []
      let apiUrl = this.$store.state.receipt.menu.fetchDepartment
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.departments.push(data)
              })
            }
          }
        })
      }
    },
    async fetchBank() {
      this.banks = []
      let apiUrl = this.$store.state.receipt.menu.fetchBank
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.banks.push(data)
              })
            }
          }
        })
      }
    },
    async fetchCashBook() {
      this.cashbooks = []
      let apiUrl = this.$store.state.receipt.menu.fetchCashBook
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.cashbooks.push(data)
              })
            }
          }
        })
      }
    },
    async fetchPayerBank() {
      this.payerbanks = []
      let apiUrl = this.$store.state.receipt.menu.fetchPayerBank
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.payerbanks.push(data)
              })
            }
          }
        })
      }
    },
    async fetchBranchWBranchUserId() {
      this.branches = []
      let apiUrl = this.$store.state.receipt.menu.fetchBranchWBranchUserId
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchUserDetails(userid) {
      let username = '-'
      let apiUrl = this.$store.state.receipt.menu.fetchUserDetails
      if (apiUrl) {
        const expand = {
          userId: userid
        }
        await this.$http.get(expandURI(apiUrl, expand)).then(result => {
          if (result.data) {
            const data = result.data.dataMap
            const { userDetails } = data
            username = userDetails.firstName
            if (userDetails.lastName !== null || userDetails.lastName !== '') {
              username = username + ' ' + userDetails.lastName
            }
          }
        })
      }
      return username
    },
    checkFiltersNullValue() {
      const ignore = ['transactionType', 'fund', 'transactionDate', 'actionName']
      return Object.keys(this.receiptsForm)
        .filter(key => !ignore.includes(key))
        .every(key => this.receiptsForm[key] === null ||
          this.receiptsForm[key] === '' ||
          this.receiptsForm[key] === undefined
        )
    },
    doExportFilters() {
      const checknull = this.checkFiltersNullValue()
      if (checknull) {
        this.exportList.promptFilterWarning = true
      } else {
        this.exportList.promptFilterWarning = false
        this.doExportList()
      }
    },
    async doExportList() {
      let columns = []
      let columnsTitle = []
      this.receiptsColumns.map(t => {
        columnsTitle.push(t.text)
        columns.push(t.value)
      })
      const apiUrl = this.$store.state.receipt.menu.exportAcctTypeFilteredList
      if (apiUrl) {
        try {
          let massageForm = clone(this.receiptsForm)
          massageForm.acctType = 'Receipt'
          massageForm.columns = columns.join(',')
          massageForm.columnsTitle = columnsTitle.join(',')
          await this.$http.post(apiUrl, massageForm)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getPaymentMethod(payment) {
      this.getCommonData('paymentmethod', payment)
    }
  },
  mixins: [
    MessageMixin,
    recordLockMixin,
    accountsAjaxCalls
  ]
}
